// Smart Asset Import — pipeline orchestrator (pure, deterministic, network-free).
//
// Ties the stages together: profile -> column-map -> per-row field assembly + master
// resolution + transforms + dedup -> route. Master data and the learned dictionary are
// INJECTED, so this whole function is unit-testable against golden files with no API or AI.
//
// Output rows carry the AssetPro template columns (names + generated codes), per-field
// detail for the review UI, and the flags that determined their routing. Every derived or
// reformatted value is recorded in the transform log.

import { profileSheet } from './sheetProfiler';
import { classifyCell, cleanString, normalizeKey } from './textNormalize';
import { mapColumns } from './columnMapper';
import { FIELD_DEFS, OUTPUT_COLUMNS, MASTER_CRITICAL_FIELDS, SOURCE_ONLY_FIELDS } from './headerAliases';
import { resolveValue, emptyMasters } from './referenceResolver';
import { normalizeDate } from './dateNormalizer';
import { parseCapacity } from './capacityParser';
import { generateLocationCode, generateAssetSubtypeCode } from './assetCodeGen';
import { buildAssetModelName } from './assetModelName';
import { createTransformLog } from './transformLog';
import { routeRow } from './confidenceRouter';

// Pull the raw source value for a canonical field from a row's cells.
const rawValueFor = (fieldKey, mapping, cells) => {
  const m = mapping[fieldKey];
  if (!m) return undefined;
  return cells[m.sourceIndex];
};

const sourceHeaderFor = (fieldKey, mapping) => (mapping[fieldKey] ? mapping[fieldKey].sourceHeader : '');

/**
 * @param {Array<Array<*>>} grid                  raw array-of-arrays
 * @param {Object} options
 * @param {Object} options.masters                from referenceResolver.fetchMasterData (or emptyMasters)
 * @param {Object<string,string>} options.learnedAliases
 * @param {Object<string,number>} options.mappingOverride  fieldKey -> sourceColumnIndex (from review)
 * @returns {{ profile, columnMap, rows, summary, transformLog }}
 */
export const processSheet = (grid, options = {}) => {
  const masters = options.masters || emptyMasters();
  const learnedAliases = options.learnedAliases || {};
  const mappingOverride = options.mappingOverride || {};
  const log = createTransformLog();

  const profile = profileSheet(grid);
  const columnMap = mapColumns(profile.headers, learnedAliases);

  // Apply manual overrides from the review screen (fieldKey -> source column index).
  // A negative index is the "unmap" sentinel: remove the auto-detected mapping entirely.
  Object.keys(mappingOverride).forEach((fieldKey) => {
    const colIndex = mappingOverride[fieldKey];
    if (colIndex === null || colIndex === undefined || colIndex < 0) {
      delete columnMap.mapping[fieldKey];
      return;
    }
    const header = (profile.headers.find((h) => h.index === colIndex) || {}).raw || '';
    columnMap.mapping[fieldKey] = { sourceIndex: colIndex, sourceHeader: header, via: 'manual', confidence: 100 };
  });

  const mapping = columnMap.mapping;

  // Recompute which source columns are unmapped and which required fields are missing AFTER
  // overrides — otherwise a column the user just mapped (or one displaced by an override)
  // would still show in the "unmapped" list. Keeps the mapping screen consistent.
  const mappedIndexes = new Set(
    Object.keys(mapping).map((fk) => mapping[fk].sourceIndex).filter((i) => i >= 0)
  );
  columnMap.unmappedSourceColumns = profile.headers
    .filter((h) => !mappedIndexes.has(h.index))
    .map((h) => ({ index: h.index, header: h.raw }));
  columnMap.unmappedRequiredFields = Object.keys(FIELD_DEFS).filter((f) => {
    const def = FIELD_DEFS[f];
    return def.required && !def.autoGenerated && !mapping[f];
  });
  const droppedSourceCols = profile.droppedEmptyColumns.length > 0;

  const rows = [];
  let snCounter = 0;

  profile.dataRows.forEach((dr) => {
    if (dr.isSeparator) {
      log.add({ rowId: `src#${dr.sourceRowIndex}`, op: 'skip', reason: 'separator/blank row', source: 'deterministic' });
      return;
    }
    const cells = dr.cells;
    const rowId = `row-${rows.length + 1}`;
    const flags = [];
    const fields = {};

    if (profile.headerAmbiguous) flags.push('HEADER_AMBIGUOUS');
    if (droppedSourceCols) {
      // Informational once per row so the user knows placeholder columns were removed.
      // (Empty placeholder columns are not data loss, but we surface it.)
    }

    // --- gather core text values ---
    const get = (fieldKey) => cleanString(rawValueFor(fieldKey, mapping, cells));
    const assetType = get('assetType');
    const assetSubType = get('assetSubType');
    const make = get('make');
    const modelNo = get('modelNo'); // optional input to the Asset Model formula
    // Asset Model is auto-created: Make + Asset Sub Type + Model No (optional).
    const model = buildAssetModelName(make, assetSubType, modelNo);
    const location = get('location');
    const subLocation = get('subLocation');
    const owner = get('owner');
    const ownerCode = get('ownerCode');
    const purchaseVendor = get('purchaseVendor');
    const purchaseInvoiceNo = get('purchaseInvoiceNo');
    const purchaseAmount = get('purchaseAmount');

    // Description: mapped description, else fall back to remark (logged), else blank.
    let description = get('description');
    if (!description) {
      const remark = get('remark');
      if (remark) {
        description = remark;
        log.add({ rowId, field: 'Description', op: 'derive', before: '', after: remark, reason: 'used Remark column (no Description present)', source: 'deterministic' });
      }
    }

    // Source-only identifiers (not template columns) — captured for dedup/visibility.
    const existingAssetCode = get('existingAssetCode');
    const altName = get('altName');
    const serial = get('serial');
    const capacityRaw = get('capacity') || assetSubType || modelNo; // capacity often in the sub-type/KVA
    const capacity = parseCapacity(capacityRaw);

    // --- Purchase Date normalization ---
    const rawDate = rawValueFor('purchaseDate', mapping, cells);
    const dateInfo = normalizeDate(rawDate);
    let purchaseDateOut = '';
    if (dateInfo.status === 'OK') {
      purchaseDateOut = dateInfo.iso;
      if (cleanString(rawDate) !== dateInfo.iso && cleanString(rawDate) !== '') {
        log.add({ rowId, field: 'Purchase Date', op: 'normalize', before: cleanString(rawDate), after: dateInfo.iso, reason: dateInfo.note, source: 'deterministic' });
      }
    } else if (dateInfo.status === 'AMBIGUOUS') {
      purchaseDateOut = cleanString(rawDate); // preserve raw; do not guess
      flags.push('AMBIGUOUS_DATE');
      log.add({ rowId, field: 'Purchase Date', op: 'flag', before: cleanString(rawDate), after: '(kept raw)', reason: dateInfo.note, source: 'deterministic' });
    } else if (dateInfo.status === 'INVALID') {
      purchaseDateOut = cleanString(rawDate);
      flags.push('INVALID_DATE');
      log.add({ rowId, field: 'Purchase Date', op: 'flag', before: cleanString(rawDate), after: '(kept raw)', reason: dateInfo.note, source: 'deterministic' });
    }

    // --- QTY: distinguish missing vs zero; never silently default ---
    const rawQty = rawValueFor('qty', mapping, cells);
    const qtyProvenance = classifyCell(rawQty);
    const hasIdentifier = !!(existingAssetCode || altName || serial || modelNo);
    let qtyOut = '';
    let qtyNum = null;
    if (qtyProvenance === 'VALUE') {
      qtyNum = parseFloat(String(rawQty).replace(/[^0-9.]/g, ''));
      qtyOut = Number.isFinite(qtyNum) ? qtyNum : cleanString(rawQty);
    } else if (qtyProvenance === 'ZERO') {
      qtyNum = 0;
      qtyOut = 0;
      flags.push('ZERO_QTY');
      log.add({ rowId, field: 'QTY', op: 'flag', before: '0', after: '0', reason: 'literal zero quantity — confirm intentional', source: 'deterministic' });
    } else {
      // EMPTY
      qtyOut = 1;
      flags.push('QTY_ASSUMED');
      log.add({ rowId, field: 'QTY', op: 'assume', before: '(empty)', after: '1', reason: 'no quantity supplied; assumed 1 unit (flagged, not auto-accepted)', source: 'deterministic' });
    }

    // --- Aggregate detection: a type+count line with no per-unit identity ---
    const isAggregate = !hasIdentifier && Number.isFinite(qtyNum) && qtyNum > 1;
    if (isAggregate) {
      flags.push('AGGREGATE');
      log.add({ rowId, field: 'QTY', op: 'flag', before: '', after: String(qtyNum), reason: 'asset type + count only; no per-unit identifiers (itemization required before per-asset creation)', source: 'deterministic' });
    }

    // --- Sn No: use source if mapped, else sequential (logged) ---
    snCounter += 1;
    const rawSn = get('snNo');
    const snNo = rawSn || snCounter;
    if (!rawSn) {
      log.add({ rowId, field: 'Sn No', op: 'generate', before: '(empty)', after: String(snCounter), reason: 'serial number not present; assigned row sequence', source: 'deterministic' });
    }

    // --- Generated codes ---
    const locationCode = generateLocationCode(location);
    const subLocationCode = generateLocationCode(subLocation);
    if (location) log.add({ rowId, field: 'Location Code', op: 'generate', before: location, after: locationCode, reason: 'derived from Location', source: 'deterministic' });
    if (subLocation) log.add({ rowId, field: 'Sub Location Code', op: 'generate', before: subLocation, after: subLocationCode, reason: 'derived from Sub Location', source: 'deterministic' });

    // Asset Subtype Code: use the mapped source value if present, otherwise generate it from
    // the Asset Sub Type name.
    const subtypeCodeRaw = get('assetSubtypeCode');
    const subtypeCodeMapped = !!subtypeCodeRaw;
    const assetSubtypeCode = subtypeCodeMapped ? subtypeCodeRaw : generateAssetSubtypeCode(assetSubType);
    if (subtypeCodeMapped) {
      log.add({ rowId, field: 'Asset Subtype Code', op: 'map', before: '', after: assetSubtypeCode, reason: 'taken from source column', source: 'deterministic' });
    } else if (assetSubType) {
      log.add({ rowId, field: 'Asset Subtype Code', op: 'generate', before: assetSubType, after: assetSubtypeCode, reason: 'generated from Asset Sub Type (no source code)', source: 'deterministic' });
    }
    if (make || assetSubType || modelNo) log.add({ rowId, field: 'Model', op: 'generate', before: [make, assetSubType, modelNo].filter(Boolean).join(' + '), after: model, reason: 'Asset Model = Make + Asset Sub Type' + (modelNo ? ' + Model No' : ' (no Model No)'), source: 'deterministic' });

    // --- Master resolution (EXACT match validation) for business-critical fields ---
    const masterValues = { assetType, assetSubType, model, make, location, owner };
    MASTER_CRITICAL_FIELDS.forEach((fk) => {
      const res = resolveValue(FIELD_DEFS[fk].masterKind, masterValues[fk], masters);
      fields[fk] = {
        outputColumn: FIELD_DEFS[fk].outputColumn,
        value: masterValues[fk],
        sourceHeader: sourceHeaderFor(fk, mapping),
        resolveStatus: res.status,
      };
      if (res.status === 'UNMATCHED') {
        flags.push('UNMATCHED_MASTER');
        log.add({ rowId, field: FIELD_DEFS[fk].outputColumn, op: 'flag', before: masterValues[fk], after: '', reason: `needs new master record (${fk})`, source: 'deterministic' });
      } else if (res.status === 'NO_MASTER') {
        flags.push('NO_MASTER');
      }
    });

    // --- Required-field presence ---
    const requiredEmpty = [];
    Object.keys(FIELD_DEFS).forEach((fk) => {
      const def = FIELD_DEFS[fk];
      if (!def.required || def.autoGenerated) return;
      let present;
      if (fk === 'snNo') present = true; // always generated
      else if (fk === 'qty') present = qtyProvenance !== 'EMPTY';
      else if (fk === 'assetSubtypeCode') present = subtypeCodeMapped || !!assetSubType; // mapped or generatable
      else present = !!cleanString(rawValueFor(fk, mapping, cells));
      if (!present) requiredEmpty.push(fk);
    });
    if (requiredEmpty.length > 0) {
      // QTY_ASSUMED already represents the qty case; only flag others as MISSING_REQUIRED.
      const others = requiredEmpty.filter((f) => f !== 'qty');
      if (others.length > 0) {
        flags.push('MISSING_REQUIRED');
        log.add({ rowId, field: others.map((f) => FIELD_DEFS[f].outputColumn).join(', '), op: 'flag', before: '(empty)', after: '', reason: 'required template field(s) empty', source: 'deterministic' });
      }
    }

    // --- Truly empty / unusable row ---
    if (!assetType && !assetSubType && !make) {
      flags.push('ROW_EMPTY');
    }

    if (droppedSourceCols) flags.push('SOURCE_COLUMNS_DROPPED');

    // --- Assemble output record (exact template columns) ---
    const output = {
      'Sn No': snNo,
      'Asset Type': assetType,
      'Asset Sub Type': assetSubType,
      'Model': model,
      'Make': make,
      'Description': description,
      ' Location': location,
      'Location Code': locationCode,
      'Sub Location': subLocation,
      'Sub Location Code': subLocationCode,
      'Owner': owner,
      'Purchase Date': purchaseDateOut,
      'Purchase Vendor': purchaseVendor,
      'Purchase Invoice No': purchaseInvoiceNo,
      'Purchase Amount': purchaseAmount,
      'Asset Subtype Code': assetSubtypeCode,
      'Owner Code': ownerCode,
      ' QTY ': qtyOut,
    };

    // Business key for dedup: first stable identifier, else composite.
    const businessKey = normalizeKey(
      existingAssetCode || altName || serial ||
      [model, location, owner, capacity.value ? `${capacity.value}${capacity.unit}` : ''].filter(Boolean).join('|')
    );

    rows.push({
      rowId,
      sourceRowIndex: dr.sourceRowIndex,
      kind: isAggregate ? 'AGGREGATE' : 'ASSET',
      output,
      fields,
      flags,
      businessKey,
      extras: { existingAssetCode, altName, serial, capacity, modelNo, subtypeCodeMapped },
    });
  });

  // --- Dedup pass: flag rows that share a business key ---
  const keyCounts = {};
  rows.forEach((r) => { if (r.businessKey) keyCounts[r.businessKey] = (keyCounts[r.businessKey] || 0) + 1; });
  rows.forEach((r) => {
    if (r.businessKey && keyCounts[r.businessKey] > 1) {
      if (!r.flags.includes('DUPLICATE')) r.flags.push('DUPLICATE');
      log.add({ rowId: r.rowId, field: '(row)', op: 'flag', before: r.businessKey, after: String(keyCounts[r.businessKey]), reason: 'duplicate business key within file', source: 'deterministic' });
    }
  });

  // --- Route every row ---
  rows.forEach((r) => {
    // Collapse repeated flags (e.g. several master fields each raising NO_MASTER) so each
    // distinct issue is represented once per row.
    r.flags = [...new Set(r.flags)];
    const routed = routeRow(r.flags);
    r.status = routed.status;
    r.reviewReasons = routed.reviewReasons;
    r.blockingReasons = routed.blockingReasons;
  });

  const summary = {
    totalRows: rows.length,
    autoAccept: rows.filter((r) => r.status === 'AUTO_ACCEPT').length,
    review: rows.filter((r) => r.status === 'REVIEW').length,
    blocked: rows.filter((r) => r.status === 'BLOCKED').length,
    aggregates: rows.filter((r) => r.kind === 'AGGREGATE').length,
    duplicates: rows.filter((r) => r.flags.includes('DUPLICATE')).length,
    unmatchedMaster: rows.filter((r) => r.flags.includes('UNMATCHED_MASTER')).length,
    headerRowIndex: profile.headerRowIndex,
    headerAmbiguous: profile.headerAmbiguous,
    droppedEmptyColumns: profile.droppedEmptyColumns,
    unmappedSourceColumns: columnMap.unmappedSourceColumns,
    unmappedRequiredFields: columnMap.unmappedRequiredFields,
    mastersAvailable: !!masters.available,
  };

  return { profile, columnMap, rows, summary, transformLog: log, outputColumns: OUTPUT_COLUMNS };
};

/**
 * Re-evaluate a row's value-dependent flags and status from its CURRENT (edited) output, so the
 * review screen updates live as the user edits cells or bulk-fills required fields. Structural
 * and identity flags (header ambiguity, dropped columns, duplicate, aggregate) are preserved from
 * the original parse; master resolution and required-field presence are recomputed; QTY/date
 * flags are kept unless that specific cell was edited.
 *
 * @param {Object} row       a pipeline row (has .output, .flags)
 * @param {(outputColumn:string)=>*} getVal  returns the effective value for an output column
 * @param {Object} masters   master-data sets (from fetchMasterData)
 * @returns {{ flags:string[], status:'AUTO_ACCEPT'|'REVIEW'|'BLOCKED' }}
 */
export const recomputeRow = (row, getVal, masters) => {
  const flags = [];
  const orig = row.output || {};
  const str = (v) => (v === null || v === undefined ? '' : String(v));
  const keep = (f) => { if ((row.flags || []).includes(f) && !flags.includes(f)) flags.push(f); };

  // Structural / identity flags are not affected by cell edits.
  ['HEADER_AMBIGUOUS', 'SOURCE_COLUMNS_DROPPED', 'AGGREGATE', 'DUPLICATE'].forEach(keep);
  // QTY and date flags persist unless that specific cell was changed.
  if (str(getVal(' QTY ')) === str(orig[' QTY '])) { keep('QTY_ASSUMED'); keep('ZERO_QTY'); }
  if (str(getVal('Purchase Date')) === str(orig['Purchase Date'])) { keep('AMBIGUOUS_DATE'); keep('INVALID_DATE'); }

  // Master-critical fields re-resolved against AssetPro data using the current values.
  MASTER_CRITICAL_FIELDS.forEach((fk) => {
    const def = FIELD_DEFS[fk];
    const res = resolveValue(def.masterKind, getVal(def.outputColumn), masters);
    if (res.status === 'UNMATCHED' && !flags.includes('UNMATCHED_MASTER')) flags.push('UNMATCHED_MASTER');
    else if (res.status === 'NO_MASTER' && !flags.includes('NO_MASTER')) flags.push('NO_MASTER');
  });

  // Required, non-auto fields must have a value now.
  let missing = false;
  Object.keys(FIELD_DEFS).forEach((fk) => {
    const def = FIELD_DEFS[fk];
    if (!def.required || def.autoGenerated) return;
    const present = fk === 'snNo' ? true : str(getVal(def.outputColumn)).trim() !== '';
    if (!present) missing = true;
  });
  if (missing) flags.push('MISSING_REQUIRED');

  if (!str(getVal('Asset Type')).trim() && !str(getVal('Asset Sub Type')).trim() && !str(getVal('Make')).trim()) {
    flags.push('ROW_EMPTY');
  }

  const uniq = [...new Set(flags)];
  return { flags: uniq, status: routeRow(uniq).status };
};
